import { BellOutlined } from '@ant-design/icons';
import { App, Badge, Button, Select, Skeleton, Tabs, Typography } from 'antd';
import { ReactComponent as NotifBellIcon } from 'assets/icons/NotifBell.svg';
import classNames from 'clsx';
import ResponsiveDrawer from 'components/ResponsiveDrawer';
import NotificationListItem from 'components/notifications/NotificationListItem';
import Colors from 'const/colors';
import { useNotificationsPanel } from 'contexts/NotificationsPanelContext';
import { useNotifications } from 'hooks/useNotifications';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Notification,
  NotificationCategory,
  NotificationStatus,
} from 'types/Notification';

const DRAWER_WIDTH = 426;
const RESPONSIVE_DRAWER_WIDTH = `min(100vw, ${DRAWER_WIDTH}px)`;
const PAGE_SIZE = 10;

const CATEGORY_OPTIONS: {
  value: NotificationCategory;
  labelKey: string;
}[] = [
  {
    value: 'all',
    labelKey: 'notification:categories.all',
  },
  {
    value: 'documents',
    labelKey: 'notification:categories.documents',
  },
  {
    value: 'inProgress',
    labelKey: 'notification:categories.inProgress',
  },
  {
    value: 'finalization',
    labelKey: 'notification:categories.finalization',
  },
];

export const NotificationPopover = () => {
  const { t } = useTranslation();
  const { isOpen, openPanel, closePanel } = useNotificationsPanel();
  const [activeTab, setActiveTab] = useState<NotificationStatus>('unread');
  const [activeCategory, setActiveCategory] =
    useState<NotificationCategory>('all');
  const { notification } = App.useApp();

  const {
    notifications,
    counts,
    categoryCounts,
    hasMore,
    isLoading,
    isSuccess,
    isFetchingNextPage,
    fetchNextPage,
    markAllAsRead,
    markNotificationAsRead,
  } = useNotifications({
    status: activeTab,
    category: activeCategory,
    limit: PAGE_SIZE,
  });

  const handleTabChange = (tab: string) => {
    setActiveTab(tab as NotificationStatus);
    setActiveCategory('all');
  };

  const markAllNotificationsAsRead = () => {
    Promise.resolve(markAllAsRead()).catch(() => undefined);
  };

  const formatCategoryLabel = (category: NotificationCategory) => {
    const option = CATEGORY_OPTIONS.find(({ value }) => value === category);
    const labelKey = option?.labelKey ?? 'notification:categories.all';

    return `${t(labelKey)} (${categoryCounts?.[category] ?? 0})`;
  };

  const markNotificationAsReadById = (notificationId: string) => {
    markNotificationAsRead(notificationId).catch(() => {
      notification.error({
        message: t('global:internet_connexion_error'),
        placement: 'bottomRight',
      });
    });
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.readDate) {
      markNotificationAsReadById(notification.id);
    }

    closePanel();
  };

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (!hasMore || isFetchingNextPage) {
      return;
    }

    const target = event.currentTarget;
    const isNearBottom =
      target.scrollTop + target.clientHeight >= target.scrollHeight - 40;

    if (!isNearBottom) {
      return;
    }

    Promise.resolve(fetchNextPage()).catch(() => undefined);
  };

  const tabs = [
    {
      key: 'unread',
      label: t('notification:tabs.unread', {
        count: counts.unread,
      }),
    },
    {
      key: 'read',
      label: t('notification:tabs.read', { count: counts.read }),
    },
  ];

  return (
    <>
      <Badge
        count={counts.unread || ''}
        size="small"
        color={Colors.BleuHellio}
        className="[&_.ant-badge-count]:top-4 [&_.ant-badge-count]:right-4 sm:[&_.ant-badge-count]:right-2 sm:[&_.ant-badge-count]:top-2"
      >
        <Button
          type="text"
          icon={<BellOutlined className="text-black !text-xl" />}
          className="!h-12 !w-12 text-black sm:!h-10 sm:!w-10 [&_.ant-btn-icon]:!h-8 [&_.ant-btn-icon]:flex [&_.ant-btn-icon]:items-center [&_.ant-btn-icon]:justify-center"
          size="large"
          onClick={openPanel}
          aria-label={t('notification:title')}
        />
      </Badge>

      <ResponsiveDrawer
        open={isOpen}
        onClose={closePanel}
        width={RESPONSIVE_DRAWER_WIDTH}
        title={t('notification:title')}
        headerVariant="blue"
        className="[&_.ant-drawer-body]:!flex [&_.ant-drawer-body]:!min-h-0 [&_.ant-drawer-body]:!flex-col [&_.ant-drawer-title]:font-fieldwork [&_.ant-drawer-title]:text-base [&_.ant-drawer-title]:leading-6 [&_.ant-drawer-title]:font-semibold [&_.ant-drawer-body]:overflow-y-hidden"
      >
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          items={tabs}
          className="shrink-0 [&_.ant-tabs-nav]:!mb-0 [&_.ant-tabs-nav]:!bg-white [&_.ant-tabs-nav]:!px-4 [&_.ant-tabs-tab]:!py-3 [&_.ant-tabs-tab-btn]:font-fieldwork max-sm:[&_.ant-tabs-tab-active_.ant-tabs-tab-btn]:font-semibold"
          tabBarExtraContent={
            activeTab === 'unread' && counts.unread > 0 ? (
              <Button
                type="text"
                className="h-auto px-0 text-xs font-normal text-bleu-hellio-6!"
                onClick={markAllNotificationsAsRead}
                aria-label={t('notification:mark_as_read')}
                title={t('notification:mark_as_read')}
              >
                {t('notification:mark_as_read')}
              </Button>
            ) : null
          }
        />

        <div
          className="border-b border-color-border bg-white px-4 pb-4 pt-3"
          data-testid="notification-category-select"
        >
          <Select<NotificationCategory>
            value={activeCategory}
            onChange={setActiveCategory}
            aria-label={t('notification:category_filter')}
            className="w-full [&_.ant-select-selection-item]:text-sm [&_.ant-select-selector]:h-8! [&_.ant-select-selector]:rounded! [&_.ant-select-selector]:border-color-border! [&_.ant-select-selector]:px-2! [&_.ant-select-selector]:py-0!"
            classNames={{
              popup: {
                root: '[&_.ant-select-item-option-selected]:bg-gris-hellio-1! [&_.ant-select-item-option-selected]:font-semibold!',
              },
            }}
            options={CATEGORY_OPTIONS.map((category) => ({
              value: category.value,
              label: formatCategoryLabel(category.value),
            }))}
          />
        </div>

        {isLoading && (
          <div className="space-y-3 p-4">
            <Skeleton active paragraph={{ rows: 2 }} title={false} />
            <Skeleton active paragraph={{ rows: 2 }} title={false} />
            <Skeleton active paragraph={{ rows: 2 }} title={false} />
          </div>
        )}

        {isSuccess && notifications.length === 0 && (
          <div className="flex min-h-0 grow flex-col items-center justify-center gap-3 px-6 text-center">
            <NotifBellIcon />
            <Typography.Text className="text-gris-clair">
              {t('notification:no_new_notification')}
            </Typography.Text>
          </div>
        )}

        <div
          className={classNames(
            'min-h-0 overflow-y-auto overscroll-contain',
            notifications.length > 0 && 'grow',
          )}
          onScroll={handleScroll}
        >
          {isSuccess &&
            notifications.map((notification) => {
              return (
                <NotificationListItem
                  key={notification.id}
                  notification={notification}
                  className={classNames(
                    'w-full cursor-pointer border-b border-color-border bg-white px-4 py-3 text-left',
                    'hover:bg-[#fafafa]',
                  )}
                  onClick={handleNotificationClick}
                  showDescription
                  onMarkAsRead={markNotificationAsReadById}
                />
              );
            })}

          {isSuccess && isFetchingNextPage && (
            <div className="space-y-3 p-4">
              <Skeleton active paragraph={{ rows: 2 }} title={false} />
            </div>
          )}
        </div>
      </ResponsiveDrawer>
    </>
  );
};
